import { create } from "zustand";
import { persist } from "zustand/middleware";

export type ProviderKind = "xtream" | "m3u";

/** An extra guide source after a playlist's own guide (Phase 3 step 3, epg/guideFeeds.ts). */
export interface GuideFeedConfig {
  /** Stable for the source's life in the list: moving or switching it keeps it. */
  id: string;
  /** The XMLTV address (.xml or .xml.gz). It can hold a login: never shown whole or logged. */
  url: string;
  /** Switched off: kept in the list, not used. */
  enabled: boolean;
  /** Minutes this source's times are moved by (Phase 3 step 4); none when 0. */
  shiftMinutes?: number;
}

export interface ProviderConfig {
  /** Stable per-provider id — also used as the API sourceId and the device
   * catalog cache scope, so multiple providers coexist on one device. */
  id: string;
  kind: ProviderKind;
  /** User-chosen display name for this playlist. */
  playlistName: string;
  /** Xtream fields (empty when kind === "m3u"). */
  xtreamBaseUrl: string;
  xtreamUsername: string;
  xtreamPassword: string;
  /** M3U URL (empty when kind === "xtream"). */
  m3uUrl: string;
  /**
   * An M3U source's guide feed, typed in by the viewer. Optional: without it
   * the guide is the one the playlist's own header names, if any. Not part of
   * the provider's identity, so changing it never re-keys the source.
   */
  epgUrl?: string;
  /**
   * Extra guide sources, in order, after the playlist's own guide (Phase 3 step 3): a
   * channel takes its programmes from the first that has it. Not part of the identity.
   */
  guideFeeds?: GuideFeedConfig[];
  /** Minutes the playlist's own guide's times are moved by (Phase 3 step 4); none when 0. */
  guideShiftMinutes?: number;
  /** Timestamp of last successful validation. */
  lastValidated: number | null;
  /** Whether the last validation succeeded. */
  isValid: boolean;
  /** Human-readable error from the last validation attempt. */
  lastError: string | null;
  /** Channels + movies + series reported by the last successful validation.
   * Undefined on providers saved before this field existed. */
  contentCount?: number;
}

/**
 * Minimum channels + movies + series a provider must actually serve before the
 * app will let you past the first-run gate.
 *
 * Connecting is not the same as working: a panel can authenticate, return an
 * empty or near-empty catalog, and leave every rail blank. Requiring real
 * content means the gate tests what the user actually came for rather than
 * whether a password was accepted.
 */
export const MIN_PROVIDER_CONTENT = 25;

/**
 * True when this provider validated successfully AND served enough content to
 * be usable. Both conditions matter: `isValid` alone passes an empty panel, and
 * a stale count alone passes one whose credentials have since expired.
 */
export function providerHasContent(provider: ProviderConfig | undefined): boolean {
  if (!provider?.isValid) return false;
  return (provider.contentCount ?? 0) >= MIN_PROVIDER_CONTENT;
}

const BASE_PROVIDER: Omit<ProviderConfig, "id" | "playlistName" | "kind"> = {
  xtreamBaseUrl: "",
  xtreamUsername: "",
  xtreamPassword: "",
  m3uUrl: "",
  lastValidated: null,
  isValid: false,
  lastError: null,
};

/**
 * Stable identity for a provider, derived from its connection details so the
 * same server/account maps to the same id across reloads and edits that only
 * touch non-identity fields (playlist name). Reuses the Web Crypto SHA-256 when
 * available with a length/fnv fallback so this never blocks on subtleness.
 */
export function providerId(input: {
  kind: ProviderKind;
  xtreamBaseUrl?: string;
  xtreamUsername?: string;
  m3uUrl?: string;
}): string {
  const parts =
    input.kind === "xtream"
      ? `xtream|${(input.xtreamBaseUrl || "").trim().toLowerCase()}|${(input.xtreamUsername || "").trim()}`
      : `m3u|${(input.m3uUrl || "").trim()}`;
  // Pure fnv-1a; deterministic, dependency-free, fine as a cache/source id.
  let hash = 0x811c9dc5;
  for (let i = 0; i < parts.length; i += 1) {
    hash ^= parts.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `${input.kind}-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

/** Build a fresh provider with an auto-generated id. */
/**
 * Strip surrounding whitespace from a source's connection details.
 *
 * Applied by the STORE — on add, on every edit, and when saved sources load —
 * so no screen has to remember to do it, and every future way of adding a
 * source gets it for free (owner 2026-09-17: "has to be done at the click of
 * add source automatically so the next new source won't have the same
 * problem"). Phone keyboards append a space after predictive input and emails
 * paste one in; with a panel that drops the connection on a wrong login, that
 * one space surfaced as "Server unreachable". Surrounding whitespace is never
 * part of a URL or an Xtream credential. The id is unaffected: providerId
 * already trims what it hashes.
 */
export function cleanConnection<T extends Partial<ProviderConfig>>(provider: T): T {
  const clean = { ...provider };
  for (const key of ["xtreamBaseUrl", "xtreamUsername", "xtreamPassword", "m3uUrl", "epgUrl"] as const) {
    const value = clean[key];
    if (typeof value === "string") (clean as Record<string, unknown>)[key] = value.trim();
  }
  return clean;
}

/** Saved extra guide sources, keeping only well-formed ones. */
/** A saved guide shift: whole minutes within 12 hours either way, else none. */
function readShift(raw: unknown): number {
  return typeof raw === "number" && Number.isInteger(raw) && Math.abs(raw) <= 720 ? raw : 0;
}

function readGuideFeeds(raw: unknown[]): GuideFeedConfig[] {
  return raw.flatMap((entry) => {
    const feed = entry as Partial<GuideFeedConfig> | null;
    if (!feed || typeof feed.id !== "string" || typeof feed.url !== "string" || !feed.url.trim()) return [];
    const shift = readShift(feed.shiftMinutes);
    return [{ id: feed.id, url: feed.url.trim(), enabled: feed.enabled !== false, ...(shift ? { shiftMinutes: shift } : {}) }];
  });
}

export function makeProvider(
  patch: Partial<Omit<ProviderConfig, "id">> = {},
  kind: ProviderKind = "xtream",
): ProviderConfig {
  const draft: ProviderConfig = {
    ...BASE_PROVIDER,
    kind,
    id: "",
    playlistName: "",
  };
  const merged = { ...draft, ...patch, kind };
  // Id derives from the connection details (identity), then a stable hash.
  merged.id = providerId(merged);
  return merged;
}

export interface SettingsStore {
  /** All configured providers, coexisting on this device. */
  providers: ProviderConfig[];
  /** Id of the provider whose catalog/Live/EPG the UI reflects. */
  activeProviderId: string | null;
  /** The active provider config, or undefined when none configured/active. */
  activeProvider: ProviderConfig | undefined;
  /** Add a provider (replaces one with the same connection id). */
  addProvider: (provider: ProviderConfig) => void;
  /** Update a subset of fields on one provider by id. */
  updateProvider: (id: string, patch: Partial<Omit<ProviderConfig, "id">>) => void;
  /** Remove a provider by id; if it was active, activate the first remaining. */
  removeProvider: (id: string) => void;
  /** Set which provider is active. */
  setActiveProvider: (id: string) => void;
  /** Clear all providers. */
  resetProviders: () => void;
  /**
   * True once any source has passed the first-run gate on this device.
   *
   * The full-screen setup is a FIRST-RUN experience, not a permanent guard: a
   * user who has already got in should land on Home and manage sources from
   * Settings, never be thrown back to a full-screen setup because a line
   * lapsed. Set automatically the first time a source qualifies; the gate then
   * only returns if every source is removed.
   */
  setupCompleted: boolean;
}

export const useSettings = create<SettingsStore>()(
  persist(
    (set) => ({
      providers: [],
      activeProviderId: null,
      activeProvider: undefined,
      setupCompleted: false,

      addProvider: (incoming) =>
        set((state) => {
          const provider = cleanConnection(incoming);
          const exists = state.providers.some((p) => p.id === provider.id);
          const providers = exists
            ? state.providers.map((p) => (p.id === provider.id ? provider : p))
            : [...state.providers, provider];
          return {
            providers,
            activeProviderId: state.activeProviderId ?? (providers.length ? provider.id : null),
            activeProvider: providers.find((p) => p.id === (state.activeProviderId ?? provider.id)),
            setupCompleted: state.setupCompleted || providers.some((p) => providerHasContent(p)),
          };
        }),

      updateProvider: (id, patch) =>
        set((state) => {
          const providers = state.providers.map((p) => (p.id === id ? { ...p, ...cleanConnection(patch) } : p));
          return {
            providers,
            // Was reading the PRE-patch list, so the active config kept the
            // stale copy after a Test Connection wrote isValid/contentCount.
            activeProvider: providers.find((p) => p.id === state.activeProviderId),
            setupCompleted: state.setupCompleted || providers.some((p) => providerHasContent(p)),
          };
        }),

      removeProvider: (id) =>
        set((state) => {
          const providers = state.providers.filter((p) => p.id !== id);
          const activeProviderId = state.activeProviderId === id
            ? (providers[0]?.id ?? null)
            : state.activeProviderId;
          return {
            providers,
            activeProviderId,
            activeProvider: providers.find((p) => p.id === activeProviderId),
          };
        }),

      setActiveProvider: (id) =>
        set((state) => ({
          activeProviderId: id,
          activeProvider: state.providers.find((p) => p.id === id),
        })),

      resetProviders: () => set({ providers: [], activeProviderId: null, activeProvider: undefined }),
    }),
    {
      name: "slique-settings",
      partialize: (state) => ({
        providers: state.providers,
        activeProviderId: state.activeProviderId,
        setupCompleted: state.setupCompleted,
      }),
      // Migrate the older single-`provider` shape into the `providers` list.
      merge: (persisted, current) => {
        const p = persisted as Record<string, unknown>;
        const legacy = p.provider as Partial<ProviderConfig> | undefined;
        const rawProviders = Array.isArray(p.providers)
          ? (p.providers as unknown as Array<Partial<ProviderConfig>>)
          : legacy
            ? [legacy]
            : [];
        // Normalize ids: anything saved without an id gets one derived from its
        // connection details.
        // Spread order matters: BASE_PROVIDER first, then the persisted fields,
        // then force required fields with a fallback so `kind`/id are never left
        // possibly-undefined by a partial persisted record.
        const providers: ProviderConfig[] = rawProviders.map((raw) => {
          const kind: ProviderKind = raw.kind === "m3u" ? "m3u" : "xtream";
          const id = (raw.id as string | undefined) ?? "";
          // Merge raw (possibly partial) over BASE_PROVIDER, forcing the
          // required string fields so a sparse persisted record still yields a
          // complete ProviderConfig.
          const cfg: ProviderConfig = {
            ...BASE_PROVIDER,
            kind,
            id,
            playlistName: raw.playlistName ?? "",
            xtreamBaseUrl: (raw.xtreamBaseUrl ?? "").trim(),
            xtreamUsername: (raw.xtreamUsername ?? "").trim(),
            xtreamPassword: (raw.xtreamPassword ?? "").trim(),
            m3uUrl: (raw.m3uUrl ?? "").trim(),
            lastValidated: typeof raw.lastValidated === "number" ? raw.lastValidated : null,
            isValid: raw.isValid === true,
            lastError: raw.lastError ?? null,
            // NOTE: this merge rebuilds ProviderConfig field by field, so any
            // field added to the interface MUST be copied here or it is
            // silently dropped on every rehydrate. contentCount gates first-run
            // entry, so losing it would re-gate a working provider on reload.
            ...(typeof raw.contentCount === "number" ? { contentCount: raw.contentCount } : {}),
            // An M3U source's typed-in guide. Missed here once (2026-09-18): the
            // guide came up empty after every restart on the phone.
            ...(typeof raw.epgUrl === "string" && raw.epgUrl.trim() ? { epgUrl: raw.epgUrl.trim() } : {}),
            // Extra guide sources (Phase 3 step 3): copied here, or they vanish on restart.
            ...(Array.isArray(raw.guideFeeds) ? { guideFeeds: readGuideFeeds(raw.guideFeeds) } : {}),
            // The own guide's shift (Phase 3 step 4), for the same reason.
            ...(readShift(raw.guideShiftMinutes) ? { guideShiftMinutes: readShift(raw.guideShiftMinutes) } : {}),
          };
          cfg.id = cfg.id || providerId(cfg);
          return cfg;
        });
        const persistedActive =
          typeof p.activeProviderId === "string" ? (p.activeProviderId as string) : null;
        const activeProviderId: string | null =
          persistedActive && providers.some((pr) => pr.id === persistedActive)
            ? persistedActive
            : providers.length
              ? (providers[0]?.id ?? null)
              : null;
        return {
          ...current,
          providers,
          activeProviderId,
          activeProvider: providers.find((pr) => pr.id === activeProviderId),
          // Same trap as contentCount above: this merge returns an explicit
          // object, so a new persisted field must be named here. Losing this
          // one would show the first-run setup again on every reload.
          // Back-compat: a device that got in before this flag existed still
          // has a qualifying source, so treat that as already onboarded.
          setupCompleted:
            p.setupCompleted === true ||
            providers.some((pr) => providerHasContent(pr)),
        };
      },
    },
  ),
);